import React from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Stack,
  IconButton,
  Box,
  Button,
} from '@mui/material';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  TelegramShareButton,
  RedditShareButton,
} from 'react-share';
import { ReactComponent as FacebookIcon } from '../../svg/Shareicons/facebook.svg';
import { ReactComponent as TwitterIcon } from '../../svg/Shareicons/twitter.svg';
import { ReactComponent as LinkedinIcon } from '../../svg/Shareicons/linkedin.svg';
import { ReactComponent as WhatsappIcon } from '../../svg/Shareicons/whatsapp.svg';
import { ReactComponent as TelegramIcon } from '../../svg/Shareicons/telegram.svg';
import { ReactComponent as RedditIcon } from '../../svg/Shareicons/reddit.svg';

interface ProfileShareModalProps {
  open: boolean;
  onClose: () => void;
  profileUrl: string;
  profileName?: string;
  profileBio?: string;
}

const ProfileShareModal: React.FC<ProfileShareModalProps> = ({
  open,
  onClose,
  profileUrl,
  profileName = '',
  profileBio = '',
}) => {
  const shareTitle = `Check out ${profileName}'s AI Persona on Soopra AI!`;
  const shareDescription = profileBio || `Chat with ${profileName}'s AI Persona and experience their unique personality and expertise.`;

  const handleCopyClick = () => {
    navigator.clipboard.writeText(profileUrl);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 2,
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255,255,255,0.85)',
        },
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogContent>
        <Typography variant="h6" textAlign="center" mb={2}>
          Share Profile
        </Typography>
        
        <Box mb={3}>
          <Typography variant="body2" color="text.secondary" mb={1} sx={{ textAlign: 'left' }}>
            Social
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
            <Stack direction="column" alignItems="center" spacing={0.5}>
              <FacebookShareButton 
                url={profileUrl} 
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { 
                    transform: 'scale(1.1)', 
                    borderColor: 'rgba(0,0,0,0.3)' 
                  } 
                }}>
                  <FacebookIcon width={40} height={40} />
                </IconButton>
              </FacebookShareButton>
              <Typography variant="caption" color="text.secondary">
                Facebook
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" spacing={0.5}>
              <TwitterShareButton
                url={profileUrl}
                title={shareTitle}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton>
                  <TwitterIcon width={40} height={40} />
                </IconButton>
              </TwitterShareButton>
              <Typography variant="caption" color="text.secondary">
                X
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" spacing={0.5}>
              <LinkedinShareButton 
                url={profileUrl} 
                source={profileUrl} 
                title={shareTitle} 
                summary={shareDescription} 
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { 
                    transform: 'scale(1.1)', 
                    borderColor: 'rgba(0,0,0,0.3)' 
                  } 
                }}>
                  <LinkedinIcon width={40} height={40} />
                </IconButton>
              </LinkedinShareButton>
              <Typography variant="caption" color="text.secondary">
                LinkedIn
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" spacing={0.5}>
              <WhatsappShareButton
                url={profileUrl}
                title={shareTitle}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton>
                  <WhatsappIcon width={40} height={40} />
                </IconButton>
              </WhatsappShareButton>
              <Typography variant="caption" color="text.secondary">
                WhatsApp
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" spacing={0.5}>
              <TelegramShareButton
                url={profileUrl}
                title={shareTitle}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton>
                  <TelegramIcon width={40} height={40} />
                </IconButton>
              </TelegramShareButton>
              <Typography variant="caption" color="text.secondary">
                Telegram
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" spacing={0.5}>
              <RedditShareButton
                url={profileUrl}
                title={shareTitle}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton>
                  <RedditIcon width={40} height={40} />
                </IconButton>
              </RedditShareButton>
              <Typography variant="caption" color="text.secondary">
                Reddit
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box mt={3}>
          <Typography variant="body2" color="text.secondary" mb={1} sx={{ textAlign: 'left' }}>
            Link
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                bgcolor: '#f5f5f5',
                px: 2,
                py: 1,
                borderRadius: 2,
                flexGrow: 1,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {profileUrl}
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={handleCopyClick}
            >
              Copy
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileShareModal;
